import React, { useState, useRef } from 'react';
import { Form, SpaceBetween, Button, Header, Select, Textarea, Alert } from '@amzn/awsui-components-react';
import updateDeviceComponentStatusHandler, {
    UpdateDeviceComponentStatusRequest,
} from '../../services/updateDeviceComponentStatusHandler';
import { OnboardingStatus } from '../../constants/onboardingStatus';
import { EventType } from '../../services/eventService';
import { logComponentInstallationEvent } from '../../utils/logComponentInstallationEvent';
import ComponentInstallationDetails from './componentInstallationDetails';
import { ComponentType } from './componentType';

const FAILURE_REASON_OPTIONS = [
    { label: 'Component defective/damaged', value: 'component_defective' },
    { label: 'Installation hardware missing', value: 'hardware_missing' },
    { label: 'Installation location inaccessible', value: 'location_inaccessible' },
    { label: 'Component incompatible with device', value: 'incompatible' },
    { label: 'Device power issues', value: 'power_issues' },
    { label: 'Device communication failure', value: 'communication_failure' },
    { label: 'Health check failed', value: 'health_check_failed' },
    { label: 'Fleet Edge software version outdated', value: 'fleet_edge_outdated' },
    { label: 'Installation Portal error', value: 'installation_portal_error' },
    { label: 'Other technical issue', value: 'other_technical' },
    { label: 'Other', value: 'other' },
];

type Props = {
    deviceId: string;
    vin: string;
    componentType: ComponentType;
    serialNumber: string;
    onFailureReported: () => void;
    onCancel: () => void;
    defaultFailureReason?: string;
    defaultNotes?: string;
};

const ReportComponentFailure = ({
    deviceId,
    vin,
    componentType,
    serialNumber,
    onFailureReported,
    onCancel,
    defaultFailureReason,
    defaultNotes,
}: Props) => {
    // Find default failure reason option if provided
    const defaultReasonOption =
        defaultFailureReason && FAILURE_REASON_OPTIONS.find((option) => option.value === defaultFailureReason);

    const [selectedReason, setSelectedReason] = useState<any>(defaultReasonOption || null);
    const [additionalNotes, setAdditionalNotes] = useState(defaultNotes || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [failureReported, setFailureReported] = useState(false);
    const reportStartTime = useRef(Date.now());

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedReason) {
            setError('Please select a failure reason');
            return;
        }

        if (selectedReason.value === 'other' && !additionalNotes.trim()) {
            setError('Please provide additional details for "Other" reason');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const failureNotes =
            selectedReason.value === 'other'
                ? `Other: ${additionalNotes.trim()}`
                : `${selectedReason.label}${additionalNotes.trim() ? ` - ${additionalNotes.trim()}` : ''}`;

        const updateRequest: UpdateDeviceComponentStatusRequest = {
            deviceId: deviceId,
            componentId: serialNumber,
            vin: vin,
            componentType: componentType.value,
            status: OnboardingStatus.FAILED,
            notes: failureNotes,
            errorMessage: selectedReason.label,
        };

        updateDeviceComponentStatusHandler(updateRequest, (apiSuccess: boolean, result: any) => {
            setIsSubmitting(false);

            if (apiSuccess) {
                // Log the failure event
                logComponentInstallationEvent({
                    deviceId,
                    vin,
                    componentType: componentType.value,
                    componentId: serialNumber,
                    eventType: EventType.FAILURE,
                    startTime: reportStartTime.current,
                    notes: failureNotes,
                    error: selectedReason.label,
                });

                setFailureReported(true);
            } else {
                setError('Failed to report failure. Please try again.');

                // Log the API failure
                logComponentInstallationEvent({
                    deviceId,
                    vin,
                    componentType: componentType.value,
                    componentId: serialNumber,
                    eventType: EventType.FAILURE,
                    startTime: reportStartTime.current,
                    error: 'Failed to update component status with failure',
                    apiError: result,
                });
            }
        });
    };

    const handleReturnToHome = () => {
        onFailureReported();
    };

    // Show the failure reported confirmation page
    if (failureReported) {
        return (
            <div>
                <Header variant="h2">Failure Reported</Header>

                <SpaceBetween size="l">
                    <Alert type="success" header="Failure Report Submitted">
                        Your component installation failure has been successfully reported to Amazon. Our support team
                        will review the issue and may contact you for additional information.
                    </Alert>

                    <ComponentInstallationDetails
                        deviceId={deviceId}
                        vin={vin}
                        componentType={componentType}
                        serialNumber={serialNumber}
                        title="Component Details"
                    />

                    <Button variant="primary" onClick={handleReturnToHome}>
                        Return to Home
                    </Button>
                </SpaceBetween>
            </div>
        );
    }

    return (
        <div>
            <Header variant="h2">Report Component Installation Failure</Header>

            {error && (
                <Alert type="error" header="Error">
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={onCancel} disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                disabled={!selectedReason || isSubmitting}
                                loading={isSubmitting}
                                formAction="submit"
                            >
                                Report Failure to Amazon
                            </Button>
                        </SpaceBetween>
                    }
                    header={<Header variant="h3">Component Installation Failure Details</Header>}
                >
                    <SpaceBetween direction="vertical" size="l">
                        <ComponentInstallationDetails
                            deviceId={deviceId}
                            vin={vin}
                            componentType={componentType}
                            serialNumber={serialNumber}
                        />

                        <div>
                            <Header variant="h3">Failure Reason *</Header>
                            <Select
                                selectedOption={selectedReason}
                                onChange={({ detail }) => {
                                    setSelectedReason(detail.selectedOption);
                                    setError(null);
                                }}
                                options={FAILURE_REASON_OPTIONS}
                                placeholder="Select the reason for installation failure"
                                selectedAriaLabel="Selected failure reason"
                            />
                        </div>

                        <div>
                            <Header variant="h3">
                                Additional Notes {selectedReason?.value === 'other' ? '*' : '(Optional)'}
                            </Header>
                            <Textarea
                                value={additionalNotes}
                                onChange={({ detail }) => {
                                    setAdditionalNotes(detail.value);
                                    setError(null);
                                }}
                                placeholder={
                                    selectedReason?.value === 'other'
                                        ? 'Please describe the issue in detail...'
                                        : 'Provide any additional details about the failure...'
                                }
                                rows={4}
                            />
                        </div>
                    </SpaceBetween>
                </Form>
            </form>
        </div>
    );
};

export default ReportComponentFailure;
